import { useEffect, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { ONE_MINUTE_IN_MILLISECONDS } from '../../consts/consts';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import {
  OBJECTIVE_TYPE_SCOPES,
  ObjectiveWithRef as Objective,
  ObjectiveTypeScope,
} from '../../utils/models/Objective';
import { useFeatures } from '../../utils/util/features';
import {
  useCurrentOrganization,
  useLocalStorageState,
} from '../../utils/util/hooks';

export interface ObjectiveType {
  id: number;
  name: string;
  description: string;
  color: string;
  scope: ObjectiveTypeScope;
  link_parent_types: number[];
}

export const INDIVIDUAL_SCOPES: ObjectiveTypeScope[] = [
  OBJECTIVE_TYPE_SCOPES.INDIVIDUAL,
];
export const STRATEGIC_SCOPES: ObjectiveTypeScope[] = [
  OBJECTIVE_TYPE_SCOPES.STRATEGIC,
];
export const ALL_SCOPES: ObjectiveTypeScope[] = [
  OBJECTIVE_TYPE_SCOPES.INDIVIDUAL,
  OBJECTIVE_TYPE_SCOPES.STRATEGIC,
];
export const NO_OBJECTIVE_TYPES: ObjectiveType[] = [];

/**
 * Serialize a list of type scopes for the `type_scopes` query param.
 * Returns undefined for an empty / nullish list so the param is omitted.
 */
export const toTypeScopesParam = (
  typeScopes: ObjectiveTypeScope[] | null | undefined
): string | undefined =>
  typeScopes && typeScopes.length > 0 ? typeScopes.join(',') : undefined;

/**
 * Serialize a list of objective type ids for the `objective_type_ids` query param.
 * Returns undefined for an empty / nullish list so the param is omitted.
 */
export const toObjectiveTypeIdsParam = (
  objectiveTypeIds: number[] | null | undefined
): string | undefined =>
  objectiveTypeIds && objectiveTypeIds.length > 0
    ? objectiveTypeIds.join(',')
    : undefined;

/**
 * Resolve the objective types that can be linked as a parent of an objective
 * of type `targetObjectiveTypeId`. Mirrors the backend fallback: a null target
 * type (legacy individual) resolves to the org's individual-scoped type, whose
 * `link_parent_types` we then expand into full `ObjectiveType` records.
 * Returns `[]` when no target type can be resolved (e.g. feature off).
 */
export const getAllowedParentTypes = (
  organizationObjectiveTypes: ObjectiveType[],
  targetObjectiveTypeId: number | null | undefined
): ObjectiveType[] => {
  const targetType =
    organizationObjectiveTypes.find((t) => t.id === targetObjectiveTypeId) ??
    organizationObjectiveTypes.find(
      (t) => t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
    );
  if (!targetType) return [];
  return targetType.link_parent_types
    .map((id) => organizationObjectiveTypes.find((t) => t.id === id))
    .filter((t): t is ObjectiveType => t !== undefined);
};

export const useCanShowParentLinking = (
  allowedParentTypes: ObjectiveType[],
  hasExistingParent: boolean
): boolean => {
  const { isOptionEnabled } = useFeatures();
  const typesEnabled = isOptionEnabled('objectives', 'objective_types_enabled');
  return !typesEnabled || allowedParentTypes.length > 0 || hasExistingParent;
};

export const useAllowedParentTypesLabel = (
  allowedParentTypes: ObjectiveType[] | undefined,
  isParentObjective: boolean
): string | null => {
  const intl = useIntl();
  const { isOptionEnabled } = useFeatures();
  const typesEnabled = isOptionEnabled('objectives', 'objective_types_enabled');
  return useMemo(() => {
    if (!isParentObjective || !typesEnabled || !allowedParentTypes?.length) {
      return null;
    }
    const individualLabel = intl.formatMessage({
      id: 'app.views.widget.inputs.objective_input.linked_objectives.tag.editing.individual_type',
      defaultMessage: 'individual',
    });
    const names = allowedParentTypes.map((t) =>
      t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL ? individualLabel : t.name
    );
    return intl.formatList(names, { type: 'disjunction', style: 'long' });
  }, [allowedParentTypes, intl, isParentObjective, typesEnabled]);
};

/**
 * Placeholder for the parent-objective search input. When we know which types
 * are valid parents for the target, list them so users don't search for
 * objectives that cannot be linked. Individual-scoped types render as
 * "individual" rather than their internal name. Falls back to "Search
 * objective" when no types are available (feature off, unknown target type,
 * or empty link_parent_types).
 */
export const getParentObjectiveSearchPlaceholder = (
  intl: IntlShape,
  allowedParentTypes: ObjectiveType[] | undefined
): string => {
  const fallback = intl.formatMessage({
    id: 'app.views.widget.inputs.objective_input.linked_objectives.tag.editing.placeholder',
    defaultMessage: 'Search objective',
  });
  if (!allowedParentTypes?.length) {
    return fallback;
  }
  const individualLabel = intl.formatMessage({
    id: 'app.views.widget.inputs.objective_input.linked_objectives.tag.editing.individual_type',
    defaultMessage: 'individual',
  });
  const names = allowedParentTypes.map((t) =>
    t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL ? individualLabel : t.name
  );
  const joined = intl.formatList(names, {
    type: 'disjunction',
    style: 'long',
  });
  return intl.formatMessage(
    {
      id: 'app.views.widget.inputs.objective_input.linked_objectives.tag.editing.placeholder_with_types',
      defaultMessage: 'Search {types} objective',
    },
    { types: joined }
  );
};

const SCOPE_SORT_LEVEL: Record<ObjectiveTypeScope, number> = {
  [OBJECTIVE_TYPE_SCOPES.STRATEGIC]: 0,
  [OBJECTIVE_TYPE_SCOPES.INDIVIDUAL]: 1,
};

/**
 * Count the transitive descendants of each type in the hierarchy graph.
 * A type B is a descendant of A when B.link_parent_types (directly or
 * through intermediate types) contains A. Self-links are ignored.
 */
export const countObjectiveTypeDescendants = <
  T extends { id: number; link_parent_types?: number[] },
>(
  types: T[]
): Map<number, number> => {
  const directChildrenByParent = new Map<number, number[]>();
  for (const t of types) {
    for (const parentId of t.link_parent_types ?? []) {
      if (parentId === t.id) continue;
      const siblings = directChildrenByParent.get(parentId) ?? [];
      siblings.push(t.id);
      directChildrenByParent.set(parentId, siblings);
    }
  }

  const memo = new Map<number, number>();
  const visit = (typeId: number, visiting: Set<number>): number => {
    const cached = memo.get(typeId);
    if (cached !== undefined) return cached;
    if (visiting.has(typeId)) return 0;
    visiting.add(typeId);
    const children = directChildrenByParent.get(typeId) ?? [];
    let total = children.length;
    for (const childId of children) {
      total += visit(childId, visiting);
    }
    visiting.delete(typeId);
    memo.set(typeId, total);
    return total;
  };

  const result = new Map<number, number>();
  for (const t of types) {
    result.set(t.id, visit(t.id, new Set()));
  }
  return result;
};

/**
 * Sort types with the canonical order: strategic-scoped first, then by
 * descendant count (most first), then alphabetically. When sorting a
 * subset, pass the full hierarchy via `hierarchySource` so descendant
 * counts reflect the whole tree, not just the subset.
 */
export const sortObjectiveTypesByScopeAndDescendants = <
  T extends {
    id: number;
    name: string;
    scope: ObjectiveTypeScope;
    link_parent_types?: number[];
  },
>(
  types: T[],
  hierarchySource?: T[]
): T[] => {
  const descendantsByTypeId = countObjectiveTypeDescendants(
    hierarchySource ?? types
  );
  return [...types].sort((a, b) => {
    const scopeDiff =
      (SCOPE_SORT_LEVEL[a.scope] ?? 3) - (SCOPE_SORT_LEVEL[b.scope] ?? 3);
    if (scopeDiff !== 0) return scopeDiff;
    const countDiff =
      (descendantsByTypeId.get(b.id) ?? 0) -
      (descendantsByTypeId.get(a.id) ?? 0);
    if (countDiff !== 0) return countDiff;
    return a.name.localeCompare(b.name);
  });
};

/** True when `type` is strategic-scoped. */
export const isStrategicScoped = (
  type: ObjectiveType | null | undefined
): boolean => type?.scope === OBJECTIVE_TYPE_SCOPES.STRATEGIC;

/**
 * True when `type` represents an individual-scoped objective — either
 * explicitly (`scope === INDIVIDUAL`) or implicitly (no type / null,
 * which is the individual default).
 */
export const isIndividualScoped = (
  type: ObjectiveType | null | undefined
): boolean => !isStrategicScoped(type);

/**
 * Derived state for a single objective item: the resolved type,
 * whether the viewer can edit it, scope flags, and the child-type
 * candidates allowed by the hierarchy.
 */
export const useObjectiveItemTypeContext = ({
  objective,
  organizationObjectiveTypes,
  userEditableObjectiveTypes,
  isGlobalEditable,
  editableObjectiveIds,
}: {
  objective: Objective;
  organizationObjectiveTypes: ObjectiveType[];
  userEditableObjectiveTypes: ObjectiveType[];
  isGlobalEditable: boolean;
  editableObjectiveIds?: ReadonlySet<number>;
}): {
  resolvedTypeId: number | null;
  resolvedType: ObjectiveType | undefined;
  isStrategic: boolean;
  isIndividual: boolean;
  childObjectiveTypes: ObjectiveType[];
  isEditable: boolean;
  canAddChild: boolean;
} => {
  const resolvedTypeId = resolveObjectiveTypeId(
    objective.objective_type,
    organizationObjectiveTypes
  );

  const resolvedType = useMemo(
    () => organizationObjectiveTypes.find((t) => t.id === resolvedTypeId),
    [organizationObjectiveTypes, resolvedTypeId]
  );

  const isStrategic = useMemo(
    () => isStrategicScoped(resolvedType),
    [resolvedType]
  );
  const isIndividual = useMemo(
    () => isIndividualScoped(resolvedType),
    [resolvedType]
  );

  const childObjectiveTypes = useMemo(
    () =>
      resolvedTypeId
        ? userEditableObjectiveTypes.filter((t) =>
            t.link_parent_types.includes(resolvedTypeId)
          )
        : userEditableObjectiveTypes,
    [userEditableObjectiveTypes, resolvedTypeId]
  );

  // Collaborator subtree inheritance: the tree response returns the exact
  // set of objective ids this user can edit via being a collaborator on
  // this objective or any ancestor (and, for individual scope, via managing
  // such a collaborator). A cheap O(1) lookup covers those pathways for
  // objectives known to the backend.
  const hasInheritedEdit = editableObjectiveIds?.has(objective.id) ?? false;

  // Optimistic-creation fallback: a brand-new client-side child won't be
  // in `editable_objective_ids` yet because the backend hasn't seen it.
  // The user only got here by clicking "Add" on an editable parent, which
  // already gates the action — trust the optimistic flag.
  const isOptimisticNew = objective.isNew === true;

  const isEditable =
    isGlobalEditable &&
    (hasInheritedEdit ||
      isOptimisticNew ||
      (isStrategic &&
        userEditableObjectiveTypes.some((t) => t.id === resolvedTypeId)));

  // No child can be created under a parent whose type is not an allowed
  // parent for the untyped (individual) child nor for any type the user can
  // create: the backend rejects every candidate type with a 400, so the add
  // affordance must not be offered.
  const canAddChild = useMemo(
    () =>
      canAddChildObjective(
        objective.objective_type,
        organizationObjectiveTypes,
        userEditableObjectiveTypes
      ),
    [
      objective.objective_type,
      organizationObjectiveTypes,
      userEditableObjectiveTypes,
    ]
  );

  return {
    resolvedTypeId,
    resolvedType,
    isStrategic,
    isIndividual,
    childObjectiveTypes,
    isEditable,
    canAddChild,
  };
};

/**
 * Resolve an objective's type id, treating null/undefined as the
 * individual type. There can only be one individual-scoped type per org.
 */
export const resolveObjectiveTypeId = (
  objectiveTypeId: number | null | undefined,
  allOrgTypes: ObjectiveType[]
): number | null => {
  if (objectiveTypeId != null) return objectiveTypeId;
  const individualType = allOrgTypes.find(
    (t) => t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
  );
  return individualType?.id ?? null;
};

/**
 * Filter types to only those that are valid parents for all given children.
 * A candidate type is valid if every child's type includes it in link_parent_types.
 * Children with no type are individual, so the individual type must include it.
 */
export const filterValidParentTypes = (
  types: ObjectiveType[],
  childrenTypeIds: (number | null | undefined)[],
  allOrgTypes: ObjectiveType[]
): ObjectiveType[] => {
  if (childrenTypeIds.length === 0) return types;

  const orgTypeById = new Map(allOrgTypes.map((t) => [t.id, t]));
  const childTypes = childrenTypeIds.map((id) => {
    const typeId = resolveObjectiveTypeId(id, allOrgTypes);
    return typeId == null ? undefined : orgTypeById.get(typeId);
  });

  return types.filter((candidate) =>
    childTypes.every(
      (childType) =>
        childType?.link_parent_types?.includes(candidate.id) ?? true
    )
  );
};

/**
 * Check whether setting an objective's type to null (None) is valid.
 *
 * "None" is the individual/untyped case. There is at most one individual-scoped
 * type per org — real (from the DB) or virtual (injected by the backend when
 * none exists). It's valid only if:
 * 1. The parent has no type, OR the individual type's link_parent_types
 *    includes the parent's type.
 * 2. All children with types allow the individual type as a parent.
 */
export const isNoneTypeValid = (
  parentTypeId: number | null | undefined,
  childrenTypeIds: (number | null | undefined)[],
  allOrgTypes: ObjectiveType[]
): boolean => {
  const individualType = allOrgTypes.find(
    (t) => t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
  );

  if (parentTypeId != null) {
    if (!individualType?.link_parent_types?.includes(parentTypeId))
      return false;
  }

  const activeChildTypeIds = childrenTypeIds.filter(
    (id): id is number => id != null
  );
  if (activeChildTypeIds.length === 0) return true;
  if (!individualType) return false;

  const orgTypeById = new Map(allOrgTypes.map((t) => [t.id, t]));
  return activeChildTypeIds.every((childTypeId) => {
    const childType = orgTypeById.get(childTypeId);
    return childType?.link_parent_types?.includes(individualType.id) ?? true;
  });
};

/**
 * Check whether a child objective can be created under a parent with the
 * given type. Mirrors the backend hierarchy validation: the new child is
 * either untyped (individual), which requires the individual type to allow
 * the parent's type as a parent, or one of the types the user can create
 * whose link_parent_types includes the parent's type. When neither exists the
 * backend rejects the creation, so the UI must not offer the action.
 */
export const canAddChildObjective = (
  parentTypeId: number | null | undefined,
  allOrgTypes: ObjectiveType[],
  userEditableTypes: ObjectiveType[]
): boolean => {
  if (parentTypeId == null) return true;
  if (isNoneTypeValid(parentTypeId, [], allOrgTypes)) return true;
  return userEditableTypes.some((t) =>
    t.link_parent_types?.includes(parentTypeId)
  );
};

const OBJECTIVE_TYPES_CACHE_TTL = 10 * ONE_MINUTE_IN_MILLISECONDS;

/**
 * Fetch objective types for the current organization.
 *
 * Returns two lists:
 * - organizationObjectiveTypes: all types (for display — badges, labels)
 * - userEditableObjectiveTypes: types the user can create (for editing)
 *
 * Both lists are cached in localStorage with a 1-hour TTL to avoid flickering.
 */
export const useObjectiveTypes = (): {
  organizationObjectiveTypes: ObjectiveType[];
  userEditableObjectiveTypes: ObjectiveType[];
  isLoading: boolean;
} => {
  const { isOptionEnabled } = useFeatures();
  const organizationId = useCurrentOrganization()?.id;
  const proxyParameters = useProxyParameters();
  const proxy = proxyParameters.proxy;
  const typesEnabled = isOptionEnabled('objectives', 'objective_types_enabled');

  // User-editable types depend on the acting user, so the cache must be
  // scoped per proxy as well — otherwise an admin's cached editable list
  // leaks into the impersonated person's session.
  const editableCacheKey = proxy
    ? `objective-types-editable-${organizationId}-${proxy}`
    : `objective-types-editable-${organizationId}`;

  const [allOrgTypes, setAllOrgTypes] = useLocalStorageState<ObjectiveType[]>(
    `objective-types-org-${organizationId}`,
    [],
    { ttlInMs: OBJECTIVE_TYPES_CACHE_TTL }
  );
  const [allEditableTypes, setAllEditableTypes] = useLocalStorageState<
    ObjectiveType[]
  >(editableCacheKey, [], {
    ttlInMs: OBJECTIVE_TYPES_CACHE_TTL,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!typesEnabled || !organizationId) {
      return;
    }

    setIsLoading(true);
    let pending = 2;
    const onDone = () => {
      pending -= 1;
      if (pending === 0) setIsLoading(false);
    };

    ConfirmAPI.sendRequestToConfirm(
      'GET',
      '/objective-types',
      { ...proxyParameters },
      (data: { results: ObjectiveType[] } | null) => {
        setAllOrgTypes(data?.results ?? []);
        onDone();
      },
      onDone
    );

    ConfirmAPI.sendRequestToConfirm(
      'GET',
      '/objective-types/user-editable',
      { ...proxyParameters },
      (data: ObjectiveType[] | null) => {
        setAllEditableTypes(data ?? []);
        onDone();
      },
      onDone
    );
  }, [
    typesEnabled,
    organizationId,
    proxyParameters,
    setAllOrgTypes,
    setAllEditableTypes,
  ]);

  const organizationObjectiveTypes = useMemo(
    () =>
      sortObjectiveTypesByScopeAndDescendants(
        allOrgTypes ?? NO_OBJECTIVE_TYPES
      ),
    [allOrgTypes]
  );

  const userEditableObjectiveTypes = useMemo(
    () =>
      sortObjectiveTypesByScopeAndDescendants(
        allEditableTypes ?? NO_OBJECTIVE_TYPES,
        organizationObjectiveTypes
      ),
    [allEditableTypes, organizationObjectiveTypes]
  );

  return {
    organizationObjectiveTypes,
    userEditableObjectiveTypes,
    isLoading,
  };
};

type StrategicAccessResponse = {
  has_strategic_view_access: boolean;
};

const STRATEGIC_ACCESS_CACHE_TTL = 10 * ONE_MINUTE_IN_MILLISECONDS;

/**
 * Whether the current user can view at least one strategic objective —
 * either via type-admin rights or via the collaborator subtree-inherit
 * pathway. Used to gate the Strategic tab on the company-objectives page.
 *
 * Cached in localStorage with a 10-minute TTL to avoid flickering on the
 * Strategic tab while the request is in flight.
 */
export const useStrategicViewAccess = (): boolean => {
  const organizationId = useCurrentOrganization()?.id;
  const proxyParameters = useProxyParameters();
  const proxy = proxyParameters.proxy;

  // Strategic access depends on the acting user, so the cache must be scoped
  // per proxy as well — otherwise an admin's cached access leaks into the
  // impersonated person's session.
  const cacheKey = proxy
    ? `objectives-strategic-access-${organizationId}-${proxy}`
    : `objectives-strategic-access-${organizationId}`;

  const [hasStrategicViewAccess, setHasStrategicViewAccess] =
    useLocalStorageState<boolean>(cacheKey, false, {
      ttlInMs: STRATEGIC_ACCESS_CACHE_TTL,
    });

  useEffect(() => {
    if (!proxyParameters.organization) {
      return;
    }
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      '/objectives/strategic-access',
      { ...proxyParameters },
      (data: StrategicAccessResponse | null) => {
        setHasStrategicViewAccess(data?.has_strategic_view_access ?? false);
      }
    );
  }, [proxyParameters, setHasStrategicViewAccess]);

  return hasStrategicViewAccess ?? false;
};
